import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  HiOutlineMap,
  HiOutlineHome,
  HiOutlineLocationMarker,
  HiOutlineShare,
  HiOutlineDownload,
  HiOutlineLockClosed,
  HiOutlineX,
  HiOutlineSearch,
  HiOutlineMenuAlt2,
  HiOutlineExternalLink,
  HiOutlinePhone,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast, { Toaster } from 'react-hot-toast'
import html2canvas from 'html2canvas'
import mapService from '../services/mapService'
import iframeDomainService from '../services/iframeDomainService'
import LeafletMapPublicView from '../components/map/LeafletMapPublicView'

// Marker type config - đồng bộ với MapEditor
const markerTypeOptions = [
  { value: 'custom', label: 'Tự do', color: '#10B981', icon: '🏷️' },
  { value: 'ban', label: 'Bán', color: '#EF4444', icon: '💰' },
  { value: 'thue', label: 'Cho thuê', color: '#3B82F6', icon: '🏠' },
  { value: 'both', label: 'Bán & Thuê', color: '#8B5CF6', icon: '🔄' },
]

function MapPublicView() {
  const { shareToken } = useParams()

  // Parse URL params for embed customization
  const searchParams = new URLSearchParams(window.location.search)
  const embedParams = {
    hideHeader: searchParams.get('header') === '0',
    hideSidebar: searchParams.get('sidebar') === '0',
    compact: searchParams.get('compact') === '1',
  }

  // Data state
  const [mapData, setMapData] = useState(null)
  const [markers, setMarkers] = useState([])
  const [paths, setPaths] = useState([]) // NEW: paths/shapes
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Password protection
  const [isProtected, setIsProtected] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')

  // Iframe domain check
  const [isInIframe, setIsInIframe] = useState(false)
  const [iframeDomainAllowed, setIframeDomainAllowed] = useState(true)
  const [iframeDomainChecked, setIframeDomainChecked] = useState(false)
  const [parentDomain, setParentDomain] = useState('')

  // UI state
  const [filterType, setFilterType] = useState('all')
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Responsive: detect screen size and iframe for default sidebar state
  const getDefaultSidebarState = useCallback(() => {
    // Always closed in iframe or embed mode
    if (window.self !== window.top || embedParams.hideSidebar) return false
    // Closed on mobile (< 1024px)
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return false
    return true
  }, [embedParams.hideSidebar])

  const [sidebarOpen, setSidebarOpen] = useState(getDefaultSidebarState)

  // Leaflet Map refs
  const leafletMapRef = useRef(null)
  const containerRef = useRef(null)

  // Check if in iframe and validate domain
  useEffect(() => {
    const checkIframeDomain = async () => {
      // Check if we're inside an iframe
      const inIframe = window.self !== window.top
      setIsInIframe(inIframe)

      if (inIframe) {
        try {
          // Try to get parent domain from referrer
          let parentHost = null
          const referrer = document.referrer
          if (referrer) {
            try {
              const url = new URL(referrer)
              parentHost = url.hostname
              setParentDomain(parentHost)
            } catch {
              // Invalid referrer URL
            }
          }

          // Check with backend if domain is allowed
          // Send the parent domain explicitly for validation
          const result = await iframeDomainService.checkCurrentDomain(parentHost)
          setIframeDomainAllowed(result.allowed)
          if (result.domain) {
            setParentDomain(result.domain)
          }
        } catch (err) {
          console.error('Failed to check iframe domain:', err)
          // Default to not allowed if check fails
          setIframeDomainAllowed(false)
        }
      }

      setIframeDomainChecked(true)
    }

    checkIframeDomain()
  }, [])

  // Load map data
  useEffect(() => {
    loadMapData()
  }, [shareToken])


  const loadMapData = async (pwd = null) => {
    setLoading(true)
    setError(null)

    try {
      const mapRes = await mapService.getPublicMap(shareToken, pwd)

      if (mapRes.status === 'success') {
        setMapData(mapRes.data)
        setIsProtected(false)

        // Load markers
        const markersRes = await mapService.getPublicMapMarkers(shareToken)
        if (markersRes.status === 'success') {
          setMarkers(markersRes.data || [])
        }

        // Load paths/shapes
        try {
          const pathsRes = await mapService.getPublicMapPaths(shareToken)
          if (pathsRes.status === 'success') {
            setPaths(pathsRes.data || [])
          }
        } catch (pathErr) {
          console.log('No paths found or paths API not available')
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        // Password protected
        setIsProtected(true)
        if (pwd) {
          setPasswordError('Mật khẩu không đúng')
        }
      } else if (err.response?.status === 404) {
        setError('Map không tồn tại hoặc chưa được xuất bản')
      } else {
        setError('Không thể tải map')
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = (e) => {
    e.preventDefault()
    if (!password.trim()) {
      setPasswordError('Vui lòng nhập mật khẩu')
      return
    }
    loadMapData(password)
  }

  // Get marker color based on type
  const getMarkerColor = useCallback((marker) => {
    if (marker.marker_color) return marker.marker_color
    switch (marker.marker_type) {
      case 'ban': return '#EF4444'
      case 'thue': return '#3B82F6'
      case 'both': return '#8B5CF6'
      case 'custom': return '#10B981'
      default: return '#6B7280'
    }
  }, [])

  const getFilteredMarkers = useCallback(() => {
    let filtered = markers.filter((m) => {
      if (filterType === 'all') return true
      if (filterType === 'ban') return m.marker_type === 'ban' || m.marker_type === 'both'
      if (filterType === 'thue') return m.marker_type === 'thue' || m.marker_type === 'both'
      if (filterType === 'custom') return m.marker_type === 'custom'
      return true
    })

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (m) =>
          m.name?.toLowerCase().includes(query) ||
          m.address?.toLowerCase().includes(query) ||
          m.description?.toLowerCase().includes(query) ||
          m.label_text?.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [markers, filterType, searchQuery])

  // Handle marker click from sidebar
  const handleMarkerClick = useCallback((marker) => {
    setSelectedMarker(marker)
    if (leafletMapRef.current) {
      leafletMapRef.current.panTo(marker.latitude, marker.longitude, 16)
      // Open popup
      setTimeout(() => {
        leafletMapRef.current.openMarkerPopup(marker.id)
      }, 500)
    }
  }, [])

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.success('Đã copy link!')
    } catch {
      toast.error('Không thể copy link')
    }
  }

  const handleExportImage = async () => {
    if (!containerRef.current) return

    try {
      toast.loading('Đang xuất ảnh...', { id: 'export' })

      const canvas = await html2canvas(containerRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
      })

      const link = document.createElement('a')
      link.download = `map-${mapData?.name || 'export'}-${Date.now()}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()

      toast.success('Đã xuất ảnh!', { id: 'export' })
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Xuất ảnh thất bại', { id: 'export' })
    }
  }

  const filteredMarkers = getFilteredMarkers()

  // Get marker type counts
  const markerCounts = {
    all: markers.length,
    custom: markers.filter((m) => m.marker_type === 'custom').length,
    ban: markers.filter((m) => m.marker_type === 'ban' || m.marker_type === 'both').length,
    thue: markers.filter((m) => m.marker_type === 'thue' || m.marker_type === 'both').length,
  }

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Đang tải map...</p>
        </div>
      </div>
    )
  }

  // Password protection
  if (isProtected) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <HiOutlineLockClosed className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Map được bảo vệ</h1>
            <p className="text-gray-500 text-sm mt-1">
              Nhập mật khẩu để xem map này
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setPasswordError('')
              }}
              placeholder="Nhập mật khẩu..."
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-3"
              autoFocus
            />
            {passwordError && (
              <p className="text-red-500 text-sm mb-3">{passwordError}</p>
            )}
            <button
              type="submit"
              className="w-full py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
            >
              Xác nhận
            </button>
          </form>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HiOutlineMap className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-500 text-sm">
            Vui lòng kiểm tra lại đường link
          </p>
        </div>
      </div>
    )
  }

  // Iframe domain not allowed
  if (isInIframe && iframeDomainChecked && !iframeDomainAllowed) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-red-50 p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HiOutlineLockClosed className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Domain chưa được đăng ký
          </h1>
          <p className="text-gray-600 text-sm mb-4">
            Website <strong className="text-orange-600">{parentDomain || 'này'}</strong> chưa được phép nhúng bản đồ qua iframe.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-left text-sm">
            <p className="font-medium text-gray-700 mb-2">Để sử dụng tính năng này:</p>
            <ol className="list-decimal list-inside text-gray-600 space-y-1">
              <li>Liên hệ Admin để đăng ký domain</li>
              <li>Chờ Admin duyệt yêu cầu</li>
              <li>Sau khi được duyệt, iframe sẽ hoạt động bình thường</li>
            </ol>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <a
              href={window.location.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <HiOutlineExternalLink className="w-4 h-4" />
              Mở trực tiếp
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Determine if we should show minimal UI (iframe or embed mode)
  const isEmbedMode = isInIframe || embedParams.compact
  const showHeader = !embedParams.hideHeader

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <Toaster position="top-center" />

      {/* Header - responsive & embed-aware */}
      {showHeader && (
        <header className={clsx(
          'bg-white border-b border-gray-200 px-3 sm:px-4 flex items-center justify-between z-20 shadow-sm',
          isEmbedMode ? 'h-10' : 'h-12 sm:h-14'
        )}>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {/* Logo - hide on embed/mobile */}
            {!isEmbedMode && (
              <div className="hidden sm:flex w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg items-center justify-center flex-shrink-0">
                <HiOutlineMap className="w-4 h-4 text-white" />
              </div>
            )}

            {/* Map name */}
            <div className="min-w-0 flex-1">
              <h1 className={clsx(
                'font-semibold text-gray-900 truncate',
                isEmbedMode ? 'text-sm' : 'text-sm sm:text-base'
              )}>
                {mapData?.name}
              </h1>
              {!isEmbedMode && mapData?.description && (
                <p className="text-xs text-gray-500 truncate hidden md:block">
                  {mapData.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* Sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={clsx(
                'p-1.5 sm:p-2 rounded-lg transition-colors',
                sidebarOpen ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-100 text-gray-600'
              )}
              title={sidebarOpen ? 'Ẩn danh sách' : 'Hiện danh sách'}
            >
              <HiOutlineMenuAlt2 className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* Share button */}
            <button
              onClick={handleCopyLink}
              className={clsx(
                'flex items-center gap-1.5 font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors',
                isEmbedMode ? 'px-2.5 py-1 text-xs' : 'px-3 sm:px-4 py-1.5 text-sm'
              )}
            >
              <HiOutlineShare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Chia sẻ</span>
            </button>

            {/* Open in new tab - for embed mode */}
            {isEmbedMode && (
              <a
                href={window.location.href.split('?')[0]}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Mở rộng"
              >
                <HiOutlineExternalLink className="w-4 h-4 text-gray-600" />
              </a>
            )}
          </div>
        </header>
      )}

      <div className="flex-1 flex overflow-hidden relative" ref={containerRef}>
        {/* Backdrop for mobile sidebar */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-20 lg:hidden transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - responsive overlay on mobile, static on desktop */}
        <aside
          className={clsx(
            'bg-white border-r border-gray-200 flex flex-col transition-all duration-300',
            // Mobile: fixed overlay
            'fixed lg:relative inset-y-0 left-0 z-30 lg:z-10',
            // Width & transform
            sidebarOpen
              ? 'w-[85vw] sm:w-80 translate-x-0'
              : 'w-0 -translate-x-full lg:w-0 lg:-translate-x-full',
            // Overflow
            !sidebarOpen && 'overflow-hidden'
          )}
        >
          {/* Mobile close button + Search */}
          <div className="p-3 border-b border-gray-100">
            {/* Mobile header with close */}
            <div className="flex items-center justify-between mb-2 lg:hidden">
              <span className="text-sm font-medium text-gray-700">Danh sách điểm</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="relative">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm..."
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Filter tabs - đồng bộ với MapEditor */}
          <div className="p-3 border-b border-gray-100">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
              {[
                { value: 'all', label: 'Tất cả', count: markerCounts.all },
                { value: 'custom', label: 'Tự do', color: '#10B981', count: markerCounts.custom },
                { value: 'ban', label: 'Bán', color: '#EF4444', count: markerCounts.ban },
                { value: 'thue', label: 'Thuê', color: '#3B82F6', count: markerCounts.thue },
              ]
                .filter((tab) => tab.value === 'all' || tab.count > 0)
                .map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setFilterType(tab.value)}
                    className={clsx(
                      'flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all',
                      filterType === tab.value
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    {tab.color && (
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1"
                        style={{ backgroundColor: tab.color }}
                      />
                    )}
                    {tab.label}
                  </button>
                ))}
            </div>
          </div>

          {/* Marker list - đồng bộ với MapEditor */}
          <div className="flex-1 overflow-y-auto">
            {filteredMarkers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <HiOutlineLocationMarker className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500">Không có dữ liệu</p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-xs text-primary-600 mt-2 hover:underline"
                  >
                    Xóa bộ lọc
                  </button>
                )}
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredMarkers.map((marker) => (
                  <div
                    key={marker.id}
                    className={clsx(
                      'p-3 rounded-xl cursor-pointer transition-all group',
                      selectedMarker?.id === marker.id
                        ? 'bg-primary-50 ring-1 ring-primary-200'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() => handleMarkerClick(marker)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      <div
                        className="w-14 h-14 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
                        style={{ backgroundColor: `${getMarkerColor(marker)}15` }}
                      >
                        {marker.image_url ? (
                          <img
                            src={marker.image_url}
                            alt={marker.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.parentElement.innerHTML = `
                                <svg class="w-6 h-6" style="color: ${getMarkerColor(marker)}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                              `
                            }}
                          />
                        ) : (
                          <HiOutlineLocationMarker
                            className="w-6 h-6"
                            style={{ color: getMarkerColor(marker) }}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {marker.name}
                        </h4>

                        {/* Type badge */}
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: getMarkerColor(marker) }}
                          />
                          <span className="text-xs text-gray-500">
                            {markerTypeOptions.find((o) => o.value === marker.marker_type)?.label}
                          </span>
                        </div>

                        {/* Custom markers: hiển thị label_text */}
                        {marker.marker_type === 'custom' && marker.label_text && (
                          <p
                            className="text-sm font-semibold mt-1 truncate"
                            style={{ color: getMarkerColor(marker) }}
                          >
                            {marker.label_text}
                          </p>
                        )}

                        {/* BĐS markers: hiển thị giá */}
                        {marker.marker_type !== 'custom' && (
                          <div className="mt-1 space-y-0.5">
                            {marker.price_text && (
                              <p className="text-sm font-semibold text-red-600 truncate">
                                {marker.price_text}
                              </p>
                            )}
                            {marker.rent_price_text && (
                              <p className="text-sm font-semibold text-blue-600 truncate">
                                {marker.rent_price_text}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Address */}
                        {marker.address && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {marker.address}
                          </p>
                        )}
                      </div>

                      {/* Quick actions */}
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {marker.external_url && (
                          <a
                            href={marker.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Xem chi tiết"
                          >
                            <HiOutlineExternalLink className="w-4 h-4 text-gray-500" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer stats - đồng bộ với MapEditor */}
          <div className="p-3 border-t border-gray-100 bg-gray-50">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Tổng: {markers.length} điểm</span>
              <span>Hiển thị: {filteredMarkers.length}</span>
            </div>
          </div>
        </aside>

        {/* Map */}
        <div className="flex-1 relative">
          {mapData && (
            <LeafletMapPublicView
              ref={leafletMapRef}
              markers={getFilteredMarkers()}
              paths={paths}
              selectedMarkerId={selectedMarker?.id}
              onMarkerClick={(marker) => setSelectedMarker(marker)}
              center={{ lat: mapData.center_lat, lng: mapData.center_lng }}
              zoom={mapData.zoom_level}
              mapStyle={mapData.map_style}
              filterType={filterType}
              getMarkerColor={getMarkerColor}
            />
          )}

          {/* Floating toggle button - shown when sidebar is closed */}
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="absolute top-3 left-3 z-10 p-2.5 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              title="Hiện danh sách"
            >
              <HiOutlineMenuAlt2 className="w-5 h-5 text-gray-700" />
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                {filteredMarkers.length} điểm
              </span>
            </button>
          )}

          {/* Legend - responsive position */}
          <div className={clsx(
            'absolute bg-white rounded-xl shadow-lg p-2.5 sm:p-3',
            // Mobile: bottom center, Desktop: bottom right
            'bottom-3 sm:bottom-4',
            'left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4',
            // Compact on mobile
            isEmbedMode && 'scale-90 origin-bottom'
          )}>
            <div className="text-xs font-medium text-gray-700 mb-1.5 sm:mb-2">Chú thích</div>
            <div className="flex sm:flex-col gap-2 sm:gap-1.5">
              {markerTypeOptions
                .filter((opt) => markerCounts[opt.value] > 0)
                .map((opt) => (
                  <div key={opt.value} className="flex items-center gap-1.5 sm:gap-2">
                    <span
                      className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: opt.color }}
                    />
                    <span className="text-[10px] sm:text-xs text-gray-600 whitespace-nowrap">{opt.label}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* View count badge */}
          {mapData?.view_count > 0 && !isEmbedMode && (
            <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-black/60 text-white text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1.5">
              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {mapData.view_count.toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MapPublicView
